import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import Load from '../Load';
import LeagueOrganizerItem from './LeagueOrganizerItem';
import SelectUserOrganizerModal from './SelectUserOrganizerModal';
import { type OrganizadorFuncao } from '../../validation/tournamentOrganizerSchema';
import {
  ORGANIZADORES_POR_LIGA,
  OrganizadoresPorLigaData,
  OrganizadoresPorLigaVars,
  criarOrganizadorLiga,
  removerOrganizador,
  Organizador,
} from '../../services/organizadorService';

interface LeagueOrganizersTabProps {
  ligaId: string;
  canEditLiga: boolean;
}

interface OrganizerItem extends Omit<Organizador, 'id'> {
  id?: string;
  tempId: string;
}

const LeagueOrganizersTab: React.FC<LeagueOrganizersTabProps> = ({
  ligaId,
  canEditLiga,
}) => {
  const {
    data: organizadoresData,
    loading: loadingOrganizadores,
    refetch,
  } = useQuery<OrganizadoresPorLigaData, OrganizadoresPorLigaVars>(
    ORGANIZADORES_POR_LIGA,
    {
      variables: { ligaId },
      skip: !ligaId,
    },
  );

  const [items, setItems] = useState<OrganizerItem[]>([]);
  const [deletingMap, setDeletingMap] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    if (organizadoresData?.organizadoresPorLiga) {
      const mapped = organizadoresData.organizadoresPorLiga.map((o) => ({
        ...o,
        tempId: o.id,
      }));
      setItems(mapped);
    }
  }, [organizadoresData]);

  const handleAddClick = () => {
    setShowAddModal(true);
  };

  const handleAddOrganizer = async (usuarioId: string, nomeUsuario: string, nomeFuncao: OrganizadorFuncao) => {
    setSaving(true);
    try {
      const created = await criarOrganizadorLiga({
        ligaId: ligaId,
        usuarioId: usuarioId,
        nomeFuncao: nomeFuncao,
        nome: nomeUsuario, // Nome do usuário selecionado
        telefone: null,
        fotoUrl: null,
      });
      if (created) {
        await refetch();
        alert('Organizador adicionado com sucesso');
      }
    } catch (e) {
      alert('Erro ao adicionar organizador');
    } finally {
      setSaving(false);
      setShowAddModal(false);
    }
  };

  const handleDelete = async (item: OrganizerItem) => {
    const nomeUsuario = item.usuario?.nome || 'este organizador';
    
    // Verificar se é o único administrador
    const administradores = items.filter(
      (i) => i.nomeFuncao === 'ADMINISTRADOR' && i.id
    );
    const isUnicoAdministrador = 
      item.nomeFuncao === 'ADMINISTRADOR' && 
      administradores.length === 1 &&
      administradores[0].id === item.id;

    if (isUnicoAdministrador) {
      alert('Não é possível remover o único administrador da liga. Deve haver pelo menos um organizador com função de Administrador.');
      return;
    }

    if (!window.confirm(`Remover organizador ${nomeUsuario}?`)) return;

    if (!item.id) {
      return;
    }

    setDeletingMap((prev) => ({ ...prev, [item.tempId]: true }));
    try {
      await removerOrganizador(item.id);
      await refetch();
      alert('Organizador removido com sucesso');
    } catch (e) {
      alert('Erro ao remover organizador');
    } finally {
      setDeletingMap((prev) => {
        const { [item.tempId]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const canDeleteOrganizer = (item: OrganizerItem): boolean => {
    // Verificar se é o único administrador
    const administradores = items.filter(
      (i) => i.nomeFuncao === 'ADMINISTRADOR' && i.id
    );
    const isUnicoAdministrador = 
      item.nomeFuncao === 'ADMINISTRADOR' && 
      administradores.length === 1 &&
      administradores[0].id === item.id;

    return !isUnicoAdministrador;
  };

  if (loadingOrganizadores) {
    return <Load />;
  }

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <button
          className="btn btn-secondary"
          onClick={handleAddClick}
          disabled={!canEditLiga || saving}
        >
          Novo organizador +
        </button>
      </div>

      <div className="row g-3">
        {items.map((item) => {
          const canDelete = canEditLiga && canDeleteOrganizer(item);
          return (
            <div key={item.tempId} className="col-12">
              <LeagueOrganizerItem
                organizador={item}
                onDelete={canDelete ? () => void handleDelete(item) : undefined}
                busy={saving || !!deletingMap[item.tempId]}
                readOnly={!canEditLiga}
                disableDeleteReason={
                  !canDelete && item.nomeFuncao === 'ADMINISTRADOR'
                    ? 'Não é possível remover o único administrador da liga'
                    : undefined
                }
              />
            </div>
          );
        })}
      </div>

      <SelectUserOrganizerModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSelect={handleAddOrganizer}
        existingOrganizerUserIds={items
          .filter((item) => item.usuario?.id)
          .map((item) => item.usuario!.id)}
      />
    </div>
  );
};

export default LeagueOrganizersTab;

